import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink } from "lucide-react";
import { FACTORY_VIDEO, STATS } from "../data/site";

export const FactoryVideo = ({ posterOverride, stats: statsProp }) => {
  const stats = statsProp || STATS;
  const [playing, setPlaying] = useState(false);
  const [posterSrc, setPosterSrc] = useState(posterOverride || FACTORY_VIDEO.poster);

  return (
    <section data-testid="factory-video-section" className=" scroll-mt-28 relative py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 max-w-3xl"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600 font-bold flex items-center gap-2">
            <span className="w-8 h-px bg-amber-500" />
            Inside Our Factory
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-bold text-navy-900 tracking-[-0.03em] leading-[1]">
            Where ideas
            <br />
            <span className="text-amber-500">become hardware.</span>
          </h2>
          <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
            A look inside our Mohali facility — SMT lines, prototype labs, QA stations and the
            engineering team that turns ambitious electronics into shipped products.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative rounded-3xl overflow-hidden bg-navy-900 shadow-2xl shadow-navy-900/20"
          data-testid="factory-video-frame"
        >
          <div className="relative aspect-[16/9] bg-navy-900">
            {!playing ? (
              <>
                <img
                  src={posterSrc}
                  alt="Factory tour preview"
                  data-testid="factory-video-poster"
                  onError={() => {
                    if (posterOverride && posterSrc === posterOverride) return;
                    if (posterSrc !== FACTORY_VIDEO.posterFallback) setPosterSrc(FACTORY_VIDEO.posterFallback);
                  }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-navy-900/30 to-navy-900/40 pointer-events-none" />

                <button
                  onClick={() => setPlaying(true)}
                  data-testid="factory-video-play"
                  aria-label="Play factory tour"
                  className="absolute inset-0 flex items-center justify-center group"
                >
                  <span className="relative flex items-center justify-center">
                    <span className="absolute w-32 h-32 rounded-full bg-amber-500/30 animate-ping" />
                    <span className="relative w-24 h-24 rounded-full bg-amber-500 group-hover:bg-amber-400 shadow-2xl shadow-amber-500/40 flex items-center justify-center transition-all group-hover:scale-110">
                      <Play className="w-9 h-9 text-navy-900 ml-1" fill="currentColor" />
                    </span>
                  </span>
                </button>

                <div className="absolute bottom-0 inset-x-0 p-5 md:p-7 flex items-end justify-between gap-4 pointer-events-none">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-amber-500 font-bold">
                      Factory Tour
                    </div>
                    <div className="mt-2 text-white font-display text-xl md:text-2xl font-bold tracking-tight">
                      {FACTORY_VIDEO.title}
                    </div>
                  </div>
                  <a
                    href={`https://youtu.be/${FACTORY_VIDEO.youtubeId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="factory-video-youtube-link"
                    className="pointer-events-auto px-3 py-1.5 rounded-md bg-white/10 backdrop-blur border border-white/20 text-white text-[11px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1.5 hover:bg-white/20 transition-colors"
                  >
                    YouTube
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </>
            ) : (
              <iframe
                src={FACTORY_VIDEO.embedUrl}
                title={FACTORY_VIDEO.title}
                data-testid="factory-video-iframe"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            )}
          </div>
        </motion.div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className="bg-white p-6 flex items-center gap-4"
              data-testid={`video-stat-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display text-2xl font-bold text-navy-900 tracking-tight leading-none">
                  {s.value}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  {s.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FactoryVideo;
